import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardApi, leadApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { KpiCard } from '../components/dashboard/KpiCard';
import {
  DollarSign,
  Briefcase,
  Percent,
  FolderKanban,
  ArrowRight,
  Phone,
  Mail,
  Calendar,
  CalendarPlus,
  FileText,
  Activity,
  Plus,
  UserPlus,
  Wallet,
} from 'lucide-react';
import { toast } from 'sonner';

const fmt = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v || 0);

const STAGE_COLORS = {
  Lead: 'border-t-slate-400',
  Qualified: 'border-t-blue-500',
  Estimate: 'border-t-violet-500',
  Proposal: 'border-t-amber-500',
  Negotiation: 'border-t-orange-500',
};

const STATUS_TEXT = {
  red: 'text-rose-600',
  yellow: 'text-amber-600',
  green: 'text-emerald-600',
  none: 'text-slate-400',
};

const RevenueToggle = ({ value, onChange }) => (
  <div
    className="inline-flex rounded-sm border border-slate-200 overflow-hidden text-xs"
    onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
    data-testid="won-revenue-toggle"
  >
    {['mtd', 'ytd'].map((v) => (
      <button
        key={v}
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange(v); }}
        className={`px-2.5 py-1 font-semibold transition-colors ${value === v ? 'bg-orange-500 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
        data-testid={`won-revenue-toggle-${v}`}
      >
        {v.toUpperCase()}
      </button>
    ))}
  </div>
);

const getActivityIcon = (type) => {
  switch (type) {
    case 'call': return Phone;
    case 'email': return Mail;
    case 'meeting': return Calendar;
    default: return FileText;
  }
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revenueView, setRevenueView] = useState('mtd');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [statsRes, leadsRes] = await Promise.all([dashboardApi.getStats(), leadApi.getAll()]);
      setStats(statsRes.data);
      setRecentLeads((leadsRes.data || []).slice(0, 5));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><span className="loading-spinner" /></div>;
  }

  const fin = stats?.financials || {};
  const sched = stats?.schedule || { days_booked: 0, days_free: 7 };
  const pipeline = stats?.pipeline_by_stage || [];
  const events = stats?.upcoming_events || [];
  const activities = stats?.recent_activities || [];
  const isOwner = stats?.user_role === 'owner';

  return (
    <div className="space-y-6 animate-slide-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-slate-500">Your business at a glance</p>
        </div>
      </div>

      {/* Top Row — Financial Snapshot */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 stagger-children">
        <KpiCard label="Pipeline Value" value={fmt(fin.pipeline_value)} icon={DollarSign} accent="blue"
          to="/leads" testId="kpi-pipeline-value" />
        <KpiCard label={`Won Revenue (${revenueView.toUpperCase()})`}
          value={fmt(revenueView === 'ytd' ? fin.won_revenue_ytd : fin.won_revenue_mtd)}
          accent="emerald" to="/reports" testId="kpi-won-revenue"
          action={<RevenueToggle value={revenueView} onChange={setRevenueView} />}
          trend={revenueView === 'mtd' ? { pct: fin.won_revenue_mtd_change_pct, label: 'vs last mo' } : undefined} />
        <KpiCard label="Active Projects" value={fin.active_projects ?? 0} icon={FolderKanban} accent="slate"
          to="/projects" testId="kpi-active-projects" />
        <KpiCard label="Avg Job Size" value={fmt(fin.avg_job_size)} icon={Briefcase} accent="violet"
          to="/reports" testId="kpi-avg-job-size" />
        <KpiCard label="Conversion Rate" value={`${fin.conversion_rate ?? 0}%`} icon={Percent} accent="orange"
          to="/reports" testId="kpi-conversion-rate" />
      </div>

      {/* 12-col body */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Main column */}
        <div className="lg:col-span-8 xl:col-span-9 flex flex-col gap-6">
          {/* Pipeline + Activity */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Sales Pipeline mini-kanban */}
            <Card className="xl:col-span-2 rounded-sm border-slate-200 shadow-sm" data-testid="dashboard-pipeline">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-bold">Sales Pipeline</CardTitle>
                  <Link to="/leads" className="text-sm text-orange-500 hover:underline flex items-center gap-1">
                    View all <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  {pipeline.map((s) => (
                    <Link
                      key={s.stage}
                      to="/leads"
                      className={`block rounded-sm border border-slate-200 border-t-2 ${STAGE_COLORS[s.stage] || 'border-t-slate-400'} p-3 hover:bg-slate-50 transition-colors`}
                      data-testid={`pipeline-stage-${s.stage.toLowerCase()}`}
                    >
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{s.stage}</p>
                      <p className={`text-xl font-extrabold tracking-tighter mt-1 ${STATUS_TEXT[s.status] || 'text-slate-900'}`}>{s.count}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{fmt(s.value)}</p>
                    </Link>
                  ))}
                </div>
                <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                    <span className="text-slate-600">On track</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
                    <span className="text-slate-600">Due soon</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-rose-500" />
                    <span className="text-slate-600">Overdue follow-up</span>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    <span className="text-slate-600">Won: {stats?.leads?.by_stage?.Won || 0}</span>
                    <span className="text-slate-300">·</span>
                    <span className="text-slate-600">Lost: {stats?.leads?.by_stage?.Lost || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className="rounded-sm border-slate-200 shadow-sm" data-testid="dashboard-activity">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-bold flex items-center gap-2">
                  <Activity className="h-4 w-4 text-slate-500" /> Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                {activities.length === 0 ? (
                  <p className="text-sm text-slate-400 py-6 text-center">No recent activity</p>
                ) : (
                  <div className="space-y-3 border-l border-slate-200 pl-3">
                    {activities.slice(0, 6).map((a, i) => {
                      const Icon = getActivityIcon(a.type);
                      return (
                        <div key={a.id || i} className="flex items-start gap-3">
                          <span className="h-7 w-7 rounded-sm bg-slate-100 flex items-center justify-center flex-shrink-0">
                            <Icon className="h-3.5 w-3.5 text-slate-500" />
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm text-slate-700 truncate">{a.description}</p>
                            <p className="text-xs text-slate-400 mt-0.5">
                              {new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Operational — Schedule + Backlog */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="rounded-sm border-slate-200 shadow-sm" data-testid="dashboard-schedule">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-bold">My Schedule This Week</CardTitle>
                  <Link to="/calendar" className="text-sm text-orange-500 hover:underline">View</Link>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-extrabold tracking-tighter text-slate-900">{sched.days_booked}</span>
                  <span className="text-slate-500 text-sm">of 7 days booked</span>
                </div>
                <div className="mt-3 grid grid-cols-7 gap-1.5">
                  {Array.from({ length: 7 }).map((_, i) => (
                    <div key={i} className={`h-2.5 rounded-sm ${i < sched.days_booked ? 'bg-orange-500' : 'bg-slate-200'}`} />
                  ))}
                </div>
                <p className="text-sm text-slate-500 mt-3">
                  {sched.days_free > 0 ? `${sched.days_free} free ${sched.days_free === 1 ? 'day' : 'days'} this week` : 'Fully booked this week'}
                </p>
              </CardContent>
            </Card>

            <Card className="rounded-sm border-slate-200 shadow-sm" data-testid="dashboard-backlog">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-bold">Backlog Revenue</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-extrabold tracking-tighter text-slate-900">{fmt(fin.backlog_revenue)}</p>
                <p className="text-sm text-slate-500 mt-2">Signed contracts not yet started</p>
                {(!fin.backlog_revenue) && (
                  <p className="text-xs text-slate-400 mt-2">
                    Set a future start date on a project to see it counted here.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Side panel */}
        <div className="lg:col-span-4 xl:col-span-3 flex flex-col gap-6">
          {/* Quick Actions */}
          <Card className="rounded-sm border-slate-200 shadow-sm" data-testid="dashboard-quick-actions">
            <CardHeader className="pb-3"><CardTitle className="text-base font-bold">Quick Actions</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <Link to="/leads/new" className="block">
                <Button className="w-full justify-start rounded-sm bg-orange-500 hover:bg-orange-600 text-white" data-testid="quick-action-add-lead">
                  <UserPlus className="h-4 w-4 mr-2" /> Add Lead
                </Button>
              </Link>
              <Link to="/projects/new" className="block">
                <Button variant="outline" className="w-full justify-start rounded-sm" data-testid="quick-action-new-project">
                  <Plus className="h-4 w-4 mr-2" /> New Project
                </Button>
              </Link>
              <Link to="/calendar" className="block">
                <Button variant="outline" className="w-full justify-start rounded-sm" data-testid="quick-action-schedule-job">
                  <CalendarPlus className="h-4 w-4 mr-2" /> Schedule Job
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Cash Flow teaser */}
          {isOwner && (
            <Card className="rounded-sm border-slate-200 shadow-sm" data-testid="dashboard-cashflow">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-bold flex items-center gap-2">
                    <Wallet className="h-4 w-4 text-slate-500" /> Cash Flow
                  </CardTitle>
                  <Link to="/invoices" className="text-sm text-orange-500 hover:underline">Invoices</Link>
                </div>
              </CardHeader>
              <CardContent className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Invoiced</span>
                  <span className="font-semibold text-slate-900">{fmt(fin.total_invoiced)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Collected</span>
                  <span className="font-semibold text-emerald-700">{fmt(fin.total_collected)}</span>
                </div>
                <div className="flex items-center justify-between border-t border-slate-100 pt-2.5">
                  <span className="text-sm text-slate-500">Outstanding</span>
                  <span className={`font-bold ${fin.overdue_amount > 0 ? 'text-rose-700' : 'text-slate-900'}`}>
                    {fmt(fin.accounts_receivable)}
                  </span>
                </div>
                {fin.overdue_amount > 0 && (
                  <div className="rounded-sm bg-rose-50 text-rose-700 text-xs font-medium px-2 py-1.5">
                    {fmt(fin.overdue_amount)} overdue
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Recent Leads */}
          <Card className="rounded-sm border-slate-200 shadow-sm" data-testid="dashboard-recent-leads">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-bold">Recent Leads</CardTitle>
                <Link to="/leads" className="text-sm text-orange-500 hover:underline">All</Link>
              </div>
            </CardHeader>
            <CardContent>
              {recentLeads.length === 0 ? (
                <div className="text-center py-6 text-slate-400 text-sm">
                  <p>No leads yet</p>
                  <Link to="/leads/new"><Button variant="link" className="text-orange-500">Add your first lead</Button></Link>
                </div>
              ) : (
                <div className="space-y-1">
                  {recentLeads.map((lead) => (
                    <Link key={lead.id} to={`/leads/${lead.id}`}
                      className="flex items-center justify-between p-2.5 rounded-sm hover:bg-slate-50 transition-colors"
                      data-testid={`lead-item-${lead.id}`}>
                      <div className="min-w-0">
                        <p className="font-medium text-sm text-slate-800 truncate">{lead.name}</p>
                        <p className="text-xs text-slate-400 truncate">{lead.project_type || 'No project type'}</p>
                      </div>
                      <Badge variant="secondary" className={`status-${lead.stage.toLowerCase()} text-xs flex-shrink-0`}>{lead.stage}</Badge>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Appointments */}
          <Card className="rounded-sm border-slate-200 shadow-sm" data-testid="dashboard-upcoming">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-bold flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-500" /> Upcoming
                </CardTitle>
                <Link to="/calendar" className="text-sm text-orange-500 hover:underline">View</Link>
              </div>
            </CardHeader>
            <CardContent>
              {events.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center">No upcoming appointments</p>
              ) : (
                <div className="space-y-2">
                  {events.map((e) => (
                    <div key={e.id} className="flex items-start gap-3 p-2.5 rounded-sm bg-slate-50">
                      <Calendar className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{e.title}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {new Date(e.start_time).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </p>
                        {e.location && <p className="text-xs text-slate-400 truncate">{e.location}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
